// @ts-check
import BaseHandler from './BaseHandler';
import { booleanPointInPolygon, point, polygon } from '@turf/turf';
import KeyCode from '../../enums/KeyCode';
import { MIN_VERTICES, TOO_FEW_POINTS, ringAreaSqMi, ringProblem } from '../../helpers/customShape';

const SOURCE_ID = 'custom-polygon-draw-source';
// @tractiq-800 / @tractiq-900 in less/variables.less.
const BLUE = '#1257dd';
const DARK_BLUE = '#0641b2';
const kind = value => ['==', 'kind', value];
const LAYERS = [
    {
        id: 'custom-polygon-draw-fill',
        type: 'fill',
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': BLUE, 'fill-opacity': 0.18 },
    },
    {
        id: 'custom-polygon-draw-line',
        type: 'line',
        filter: ['all', ['!=', '$type', 'Point'], ['!=', 'kind', 'editing']],
        paint: { 'line-color': BLUE, 'line-width': 2 },
    },
    {
        id: 'custom-polygon-draw-line-editing',
        type: 'line',
        filter: ['all', ['!=', '$type', 'Point'], ['==', 'kind', 'editing']],
        paint: { 'line-color': DARK_BLUE, 'line-width': 3 },
    },
    {
        id: 'custom-polygon-draw-midpoint',
        type: 'circle',
        filter: kind('midpoint'),
        paint: {
            'circle-radius': 4,
            'circle-color': '#a8c2f3',
            'circle-stroke-color': BLUE,
            'circle-stroke-width': 1,
        },
    },
    {
        id: 'custom-polygon-draw-vertex',
        type: 'circle',
        filter: kind('vertex'),
        paint: {
            'circle-radius': 4,
            'circle-color': BLUE,
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 1,
        },
    },
    {
        id: 'custom-polygon-draw-handle',
        type: 'circle',
        filter: kind('handle'),
        paint: {
            'circle-radius': 6,
            'circle-color': '#fff',
            'circle-stroke-color': BLUE,
            'circle-stroke-width': 2,
        },
    },
    {
        id: 'custom-polygon-draw-closing',
        type: 'circle',
        filter: kind('closing'),
        paint: {
            'circle-radius': 8,
            'circle-color': '#fff',
            'circle-stroke-color': BLUE,
            'circle-stroke-width': 3,
        },
    },
];
// terra-draw hit-tests at 40px, sized for its own big handles. Ours are
// 6-8px circles, and a midpoint sits half an edge from two vertices, so 40px
// would make short edges unclickable; 14px is a fingertip around a 12px handle.
const HIT_DISTANCE_PX = 14;
// dragonfly only fires `click` when mouseup lands on the exact mousedown
// pixel, so a hand tremor drops the vertex. We read mousedown/mouseup
// ourselves and treat anything under this many pixels as a click; more is a pan.
const CLICK_TOLERANCE_PX = 6;

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const isTyping = event => {
    const tag = event.target && event.target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

/**
 * Draws and edits the custom shape of the open analysis, after terra-draw's
 * polygon mode (the QA tool's "Measure"). Drawing: each click commits a vertex
 * and the filled shape follows the cursor; once there are three, clicking the
 * first or last point (or Enter) closes it. Editing: drag a vertex, click a
 * midpoint to add one, right-click a vertex to remove it, or, with Move on,
 * drag the shape. Done/Enter emits `POLYGON_DRAW_RESPONSE` with the ring as
 * `[lng, lat][]` (unclosed); Cancel/Esc emits `POLYGON_DRAW_CANCELLED`. The
 * analysis changes only on Done. `POLYGON_DRAW_STATE` feeds the toolbar.
 */
class PolygonDrawHandler extends BaseHandler {
    constructor(mapViewer) {
        super(mapViewer);
        this.setGluBusEvents({
            POLYGON_DRAW_REQUEST: this.start,
            POLYGON_DRAW_CANCEL: this.cancel,
            POLYGON_DRAW_CONFIRM: this.confirm,
            POLYGON_DRAW_MOVE_TOGGLE: this.toggleMove,
            POLYGON_DRAW_STATE_REQUEST: this._emitState,
            EXIT_LOCATION_ANALYSIS_MODE: this.cancel,
            // A new pin, a search result or a radius / drive time applied leaves the shape behind.
            ENTER_UPDATE_LOCATION_ANALYSIS_MODE: this.cancel,
            SET_LOCATION_ANALYSIS_TYPE_AND_SELECTION: this.cancel,
            SET_LOCATION_ANALYSIS_SELECTION: this.cancel,
        });
        /** @type {'draw' | 'edit' | null} */
        this._mode = null;
        /** @type {[number, number][]} */
        this._vertices = [];
        /** @type {[number, number] | undefined} */
        this._cursor = undefined;
        this._snapped = false;
        /** @type {{x: number, y: number} | undefined} */
        this._downPoint = undefined;
        /** @type {{type: 'vertex' | 'shape', index?: number, start?: {lng: number, lat: number}, before: [number, number][]} | null} */
        this._drag = null;
        this._panDisabled = false;
        this._moving = false;
        /** @type {string | null} */
        this._error = null;
        this._frame = 0;
    }

    /** @param {{mapInstanceId?: string, ring?: [number, number][]}} [payload] an existing ring opens in edit mode */
    start = payload => {
        if (payload && payload.mapInstanceId && payload.mapInstanceId !== this.mapInstanceId) return;
        if (this._mode) this._stopListening();
        this._clearLayers();
        const ring = payload && payload.ring && payload.ring.length >= MIN_VERTICES ? payload.ring : null;
        this._vertices = ring ? ring.map(([lng, lat]) => [lng, lat]) : [];
        this._mode = ring ? 'edit' : 'draw';
        this._cursor = undefined;
        this._snapped = false;
        this._moving = false;
        this._error = ring ? ringProblem(this._vertices) : null;
        // The map's mousedown does not take focus, so a search box left focused
        // would swallow Enter/Esc (and re-run its search on Enter).
        const active = document.activeElement;
        if (active && active !== document.body && typeof active.blur === 'function') active.blur();
        // Mutes facility popups, as the annotator does, so a vertex on a pin is just a vertex.
        this.emit('MAP_DISABLE_FEATURE_INTERACTIVITY_REQUEST', { mapInstanceId: this.mapInstanceId });
        this._setCursor(this._mode === 'draw' ? 'crosshair' : '');
        this.map.doubleClickZoom.disable();
        this.map.on('mousedown', this.onMouseDown);
        this.map.on('mouseup', this.onMouseUp);
        this.map.on('mousemove', this.onMouseMove);
        this.map.on('contextmenu', this.onContextMenu);
        document.addEventListener('mouseup', this.onDocumentMouseUp);
        // Window capture runs ahead of the search result list's document-capture
        // listener, which would otherwise take Enter and re-run its search.
        window.addEventListener('keydown', this.onKeyDown, true);
        this._render();
    };

    cancel = () => {
        if (!this._mode) return;
        this._stopListening();
        this.emit('POLYGON_DRAW_CANCELLED', { mapInstanceId: this.mapInstanceId });
    };

    // Done: close the drawing if it is still open, then hand the ring over.
    confirm = () => {
        if (!this._mode || this._drag) return;
        const problem = ringProblem(this._vertices);
        if (problem) {
            this._setError(problem);
            return;
        }
        const ring = this._vertices.slice();
        this._stopListening();
        this.emit('POLYGON_DRAW_RESPONSE', { mapInstanceId: this.mapInstanceId, ring, reshape: true });
    };

    toggleMove = () => {
        if (this._mode !== 'edit') return;
        this._moving = !this._moving;
        this._emitState();
    };

    remove() {
        if (this._mode) this._stopListening();
    }

    onMouseDown = e => {
        if (e.originalEvent && e.originalEvent.button !== 0) return;
        this._downPoint = e.point;
        if (this._mode !== 'edit') return;

        const hit = this._hitTest(e.point);
        const before = this._vertices.slice();
        if (hit && hit.type === 'midpoint') {
            this._vertices.splice(hit.index + 1, 0, hit.lngLat);
            this._drag = { type: 'vertex', index: hit.index + 1, before };
        } else if (hit && hit.type === 'vertex') {
            this._drag = { type: 'vertex', index: hit.index, before };
        } else if (this._moving && this._inside(e.lngLat)) {
            this._drag = { type: 'shape', start: e.lngLat, before };
        }
        if (this._drag) this._render();
    };

    onMouseUp = e => {
        if (this._drag) {
            this._endDrag();
            return;
        }
        const down = this._downPoint;
        this._downPoint = undefined;
        if (e.originalEvent && e.originalEvent.button !== 0) return;
        if (!down || dist(down, e.point) > CLICK_TOLERANCE_PX) return;
        if (this._mode === 'draw') this._addVertex(e);
    };

    // A release outside the map never reaches the map's own mouseup.
    onDocumentMouseUp = () => {
        if (this._drag) this._endDrag();
    };

    onMouseMove = e => {
        if (this._drag) {
            this._dragTo(e.lngLat);
        } else if (this._mode === 'draw') {
            this._cursor = [e.lngLat.lng, e.lngLat.lat];
            this._snapped = this._closingHit(e.point);
            this._setCursor(this._snapped ? 'pointer' : 'crosshair');
        } else {
            this._hover(e);
            return;
        }
        this._scheduleRender();
    };

    onContextMenu = e => {
        if (this._mode !== 'edit' || this._drag) return;
        const hit = this._hitTest(e.point);
        if (!hit || hit.type !== 'vertex') return;
        if (this._vertices.length <= MIN_VERTICES) {
            this._setError(TOO_FEW_POINTS);
            return;
        }
        const next = this._vertices.filter((_, i) => i !== hit.index);
        this._commitEdit(next, this._vertices);
    };

    onKeyDown = event => {
        if (isTyping(event)) return;
        if (event.code === KeyCode.ESC) {
            event.stopPropagation();
            this.cancel();
        } else if (event.code === KeyCode.ENTER) {
            event.preventDefault();
            event.stopPropagation();
            if (this._mode === 'draw') this._close();
            else this.confirm();
        }
    };

    _addVertex(e) {
        if (this._closingHit(e.point)) {
            this._close();
            return;
        }
        const last = this._vertices[this._vertices.length - 1];
        if (last && dist(this.map.project(last), e.point) < 1) return;
        this._vertices = [...this._vertices, [e.lngLat.lng, e.lngLat.lat]];
        this._render();
    }

    _close() {
        if (this._vertices.length < MIN_VERTICES) return;
        const problem = ringProblem(this._vertices);
        if (problem) {
            this._setError(problem);
            return;
        }
        this._mode = 'edit';
        this._cursor = undefined;
        this._snapped = false;
        this._error = null;
        this._setCursor('');
        this._render();
    }

    /** @param {{x: number, y: number}} p */
    _closingHit(p) {
        const n = this._vertices.length;
        if (this._mode !== 'draw' || n < MIN_VERTICES) return false;
        return [this._vertices[0], this._vertices[n - 1]].some(
            v => dist(this.map.project(v), p) <= HIT_DISTANCE_PX,
        );
    }

    /**
     * Nearest vertex or midpoint within the hit distance; a vertex wins a tie.
     * @param {{x: number, y: number}} p
     */
    _hitTest(p) {
        let best = null;
        const n = this._vertices.length;
        this._vertices.forEach((v, index) => {
            const d = dist(this.map.project(v), p);
            if (d <= HIT_DISTANCE_PX && (!best || d < best.d)) best = { type: 'vertex', index, d };
        });
        this._vertices.forEach((v, index) => {
            const w = this._vertices[(index + 1) % n];
            /** @type {[number, number]} */
            const lngLat = [(v[0] + w[0]) / 2, (v[1] + w[1]) / 2];
            const d = dist(this.map.project(lngLat), p);
            if (d <= HIT_DISTANCE_PX && (!best || d < best.d)) {
                best = { type: 'midpoint', index, lngLat, d };
            }
        });
        return best;
    }

    _inside(lngLat) {
        return booleanPointInPolygon(
            point([lngLat.lng, lngLat.lat]),
            polygon([[...this._vertices, this._vertices[0]]]),
        );
    }

    // dragonfly's drag-pan arms itself on mousedown before the map's own
    // mousedown fires, so disabling it there is too late. It is switched off
    // while the pointer rests on something grabbable instead.
    _hover(e) {
        const hit = this._hitTest(e.point);
        const grabShape = !hit && this._moving && this._inside(e.lngLat);
        this._setPan(!hit && !grabShape);
        this._setCursor(hit ? 'pointer' : grabShape ? 'move' : '');
    }

    _setPan(enabled) {
        const { dragPan } = this.map;
        if (!enabled && dragPan.isEnabled()) {
            dragPan.disable();
            this._panDisabled = true;
        } else if (enabled && this._panDisabled) {
            dragPan.enable();
            this._panDisabled = false;
        }
    }

    _dragTo(lngLat) {
        const drag = this._drag;
        if (drag.type === 'vertex') {
            this._vertices[drag.index] = [lngLat.lng, lngLat.lat];
            return;
        }
        const dLng = lngLat.lng - drag.start.lng;
        const dLat = lngLat.lat - drag.start.lat;
        this._vertices = drag.before.map(([lng, lat]) => [lng + dLng, lat + dLat]);
    }

    _endDrag() {
        const { before } = this._drag;
        this._drag = null;
        this._downPoint = undefined;
        this._commitEdit(this._vertices, before);
    }

    // An edit that breaks a valid shape is put back, with the reason.
    _commitEdit(next, before) {
        const problem = ringProblem(next);
        this._vertices = problem && !ringProblem(before) ? before : next;
        this._error = problem;
        this._render();
    }

    _setError(message) {
        this._error = message;
        this._emitState();
    }

    _setCursor(cursor) {
        this.map.getCanvas().style.cursor = cursor;
    }

    _stopListening() {
        window.cancelAnimationFrame(this._frame);
        this._frame = 0;
        this._mode = null;
        this._drag = null;
        this._vertices = [];
        this._error = null;
        this._setCursor('');
        this._setPan(true);
        this._clearLayers();
        // Re-enabled on the next tick: the map's own `click` follows the
        // finishing mouseup, and must neither zoom nor open the pin under it.
        // A restart in the meantime keeps them muted.
        setTimeout(() => {
            if (this._mode) return;
            this.map.doubleClickZoom.enable();
            this.emit('MAP_ENABLE_FEATURE_INTERACTIVITY_REQUEST', { mapInstanceId: this.mapInstanceId });
        }, 0);
        this.map.off('mousedown', this.onMouseDown);
        this.map.off('mouseup', this.onMouseUp);
        this.map.off('mousemove', this.onMouseMove);
        this.map.off('contextmenu', this.onContextMenu);
        document.removeEventListener('mouseup', this.onDocumentMouseUp);
        window.removeEventListener('keydown', this.onKeyDown, true);
        this._emitState();
    }

    _emitState = () => {
        const drawing = this._mode === 'draw';
        const preview = drawing && this._cursor && !this._snapped
            ? [...this._vertices, this._cursor]
            : this._vertices;
        this.emit('POLYGON_DRAW_STATE', {
            mapInstanceId: this.mapInstanceId,
            mode: this._mode,
            areaSqMi: this._mode ? ringAreaSqMi(preview) : 0,
            vertexCount: this._vertices.length,
            moving: this._moving,
            error: this._error,
        });
    };

    // One redraw per animation frame; a mousemove can fire far faster than that.
    _scheduleRender() {
        if (this._frame) return;
        this._frame = window.requestAnimationFrame(() => {
            this._frame = 0;
            if (this._mode) this._render();
        });
    }

    _clearLayers() {
        LAYERS.forEach(({ id }) => {
            if (this.map.getLayer(id)) this.map.removeLayer(id);
        });
        if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID);
    }

    _features() {
        const drawing = this._mode === 'draw';
        const n = this._vertices.length;
        const shape = drawing && this._cursor && !this._snapped
            ? [...this._vertices, this._cursor]
            : this._vertices;
        const feature = (geometry, properties) => ({ type: 'Feature', properties, geometry });
        const features = [];
        if (shape.length >= MIN_VERTICES) {
            features.push(
                feature(
                    { type: 'Polygon', coordinates: [[...shape, shape[0]]] },
                    { kind: drawing ? 'drawing' : 'editing' },
                ),
            );
        } else if (shape.length === 2) {
            features.push(feature({ type: 'LineString', coordinates: shape }, { kind: 'drawing' }));
        }
        this._vertices.forEach((coordinates, i) => {
            let pointKind = 'handle';
            if (drawing) pointKind = n >= MIN_VERTICES && (i === 0 || i === n - 1) ? 'closing' : 'vertex';
            features.push(feature({ type: 'Point', coordinates }, { kind: pointKind }));
        });
        if (!drawing && !this._drag) {
            this._vertices.forEach((v, i) => {
                const w = this._vertices[(i + 1) % n];
                features.push(
                    feature(
                        { type: 'Point', coordinates: [(v[0] + w[0]) / 2, (v[1] + w[1]) / 2] },
                        { kind: 'midpoint' },
                    ),
                );
            });
        }
        return { type: 'FeatureCollection', features };
    }

    _render() {
        const data = this._features();
        const source = this.map.getSource(SOURCE_ID);
        if (source) {
            // @ts-ignore geojson source
            source.setData(data);
        } else {
            this.map.addSource(SOURCE_ID, { type: 'geojson', data });
            LAYERS.forEach(layer =>
                this.map.addLayer({
                    ...layer,
                    source: SOURCE_ID,
                    minzoom: 0,
                    maxzoom: 24,
                    metadata: { socialexplorer: {}, isDescriptionLayer: true },
                }),
            );
        }
        this._emitState();
    }
}

export default PolygonDrawHandler;
